import {
  FloatingFormItem,
  Group,
  InputDecimal,
  SaveButton,
} from '@/components/_common';
import CfopSelectField from '@/components/_common/CfopSelectField';
import {
  TaxeIcmsOriginSelect,
  TaxeIcmsCstSelect,
  TaxeIcmsCsosnSelect,
  TaxePisCstSelect,
  TaxeCofinsCstSelect,
} from '@/components/_common/TaxesSelect';
import {
  CouponCompanyConfig,
  CouponCompanyPartConfig,
} from '@/core/domain/coupon/company/company.types';
import {
  useDefaultEmptyFieldError,
  useDefaultMaxLengthFieldError,
} from '@/hooks';
import { Col, Form, Input, Row, Typography } from 'antd';
import { TaxCouponItemCofinsCst } from '@/core/domain/coupon/coupons/coupons.item.types';
import { useCompanyFormContext } from '../../CompaniesFormProvider';
import { useEffect } from 'react';
import { useCouponCompanyConfigPart } from '@/core/domain/coupon/company/company.hooks';

interface Props {
  settings: CouponCompanyConfig;
}

const CompanyCouponForm: React.FC<Props> = ({ settings }) => {
  const [form] = Form.useForm<CouponCompanyPartConfig>();
  const ruleRequired = useDefaultEmptyFieldError();

  const { company } = useCompanyFormContext();

  const couponCompanyConfigPart = useCouponCompanyConfigPart();
  const defaultMaxLenghtFieldError = useDefaultMaxLengthFieldError();

  const hasSimpleNational = company?.taxRegimeId === 2;

  const taxesCofinsCst = Form.useWatch(
    ['defaultTaxConfig', 'cofins', 'cst'],
    form,
  );

  useEffect(() => {
    if (settings) {
      form.setFieldsValue({
        defaultTaxConfig: settings.defaultTaxConfig.partConfig,
      });
    }
  }, [settings]);

  const optionalCst = [
    TaxCouponItemCofinsCst.TAXABLE_OPERATION_NORMAL_RATE,
    TaxCouponItemCofinsCst.TAXABLE_OPERATION_DIFFERENTIATED_RATE,
    TaxCouponItemCofinsCst.TAXABLE_OPERATION_QUANTITY_RATE,
    TaxCouponItemCofinsCst.TAXABLE_OPERATION_SUBSTITUTION,
    TaxCouponItemCofinsCst.OTHER_OPERATIONS,
  ];

  const cstRequired = !!taxesCofinsCst && !optionalCst.includes(taxesCofinsCst);

  async function onSubmit(values: CouponCompanyPartConfig) {
    try {
      if (company) {
        await couponCompanyConfigPart.mutateAsync({
          companyId: company?.id,
          ...values,
        });
      }
    } catch {}
  }

  return (
    <Form form={form} onFinish={onSubmit}>
      <Group position="between" style={{ marginBottom: 24 }}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          Configuração de tributação dos itens
        </Typography.Title>

        <SaveButton
          htmlType="submit"
          loading={couponCompanyConfigPart.isLoading}
        >
          Salvar
        </SaveButton>
      </Group>

      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Typography.Title level={5}>Geral</Typography.Title>
          <Row gutter={[16, 16]}>
            <Col span={24} md={12} lg={16}>
              <FloatingFormItem
                name={['defaultTaxConfig', 'cfopId']}
                label="CFOP"
              >
                <CfopSelectField />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={8}>
              <FloatingFormItem
                name={['defaultTaxConfig', 'unitMeasure']}
                label="Unidade da peça"
                rules={[defaultMaxLenghtFieldError(100)]}
              >
                <Input />
              </FloatingFormItem>
            </Col>
          </Row>
        </Col>

        <Col span={24}>
          <Typography.Title level={5}>ICMS</Typography.Title>
          <Row gutter={[16, 16]}>
            <Col span={24} md={12} lg={8}>
              <FloatingFormItem
                name={['defaultTaxConfig', 'icms', 'origin']}
                label="Origem"
              >
                <TaxeIcmsOriginSelect allowClear />
              </FloatingFormItem>
            </Col>

            {!hasSimpleNational && (
              <Col span={24} md={12} lg={8}>
                <FloatingFormItem
                  name={['defaultTaxConfig', 'icms', 'cst']}
                  label="CST"
                >
                  <TaxeIcmsCstSelect allowClear />
                </FloatingFormItem>
              </Col>
            )}

            {hasSimpleNational && (
              <Col span={24} md={12} lg={8}>
                <FloatingFormItem
                  name={['defaultTaxConfig', 'icms', 'csosn']}
                  label="CSOSN"
                >
                  <TaxeIcmsCsosnSelect allowClear />
                </FloatingFormItem>
              </Col>
            )}

            <Col span={24} md={12} lg={8}>
              <FloatingFormItem
                name={['defaultTaxConfig', 'icms', 'rate']}
                label="Alíquota(%)"
              >
                <InputDecimal />
              </FloatingFormItem>
            </Col>
          </Row>
        </Col>

        <Col span={24}>
          <Typography.Title level={5}>PIS</Typography.Title>
          <Row gutter={16}>
            <Col span={24} md={12} lg={8}>
              <FloatingFormItem
                name={['defaultTaxConfig', 'pis', 'cst']}
                label="CST"
              >
                <TaxePisCstSelect allowClear />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={8}>
              <FloatingFormItem
                name={['defaultTaxConfig', 'pis', 'rate']}
                label="Alíquota(%)"
              >
                <InputDecimal />
              </FloatingFormItem>
            </Col>
          </Row>
        </Col>

        <Col span={24}>
          <Typography.Title level={5}>COFINS</Typography.Title>
          <Row gutter={16}>
            <Col span={24} md={12} lg={8}>
              <FloatingFormItem
                name={['defaultTaxConfig', 'cofins', 'cst']}
                label="CST"
              >
                <TaxeCofinsCstSelect allowClear />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={8}>
              <FloatingFormItem
                name={['defaultTaxConfig', 'cofins', 'rate']}
                label="Alíquota(%)"
                rules={cstRequired ? [ruleRequired] : []}
              >
                <InputDecimal />
              </FloatingFormItem>
            </Col>
          </Row>
        </Col>
      </Row>
    </Form>
  );
};

export default CompanyCouponForm;
